import { useCallback, useEffect, useRef, useState } from 'react';

import { API } from '../network/api';
import { AxiosRequestConfig, AxiosResponseHeaders } from 'axios';

const config = {} as AxiosRequestConfig;

/**
 * @function useQuery
 * @description A custom hook for fetching data from a specified API endpoint using Axios.
 *
 * This hook manages the loading state, handles errors, and provides a way to refetch data.
 *
 * @param {string} url - The API endpoint from which to fetch data.
 * @param {AxiosRequestConfig} [options] - Optional Axios request configuration.
 *                                         Avoid passing `{}` as it creates new memory references on each render,
 *                                         potentially leading to an infinite loop.
 * @param {boolean} [enabled=true] - A flag to enable or disable the fetch request.
 *                                    If set to `false`, the request will not be made.
 *
 * @returns {Object} An object containing the following properties:
 *
 * - `isLoading` {boolean} - Indicates whether the request is currently loading.
 * - `data` {T | null} - The data returned by the server, or `null` if not yet loaded.
 * - `error` {string | null} - An error message if the request fails, or `null` if no error occurred.
 * - `setData` {function} - Function to manually set the `data` property.
 * - `setError` {function} - Function to manually set the `error` property.
 * - `setLoading` {function} - Function to manually set the `isLoading` property.
 * - `refetch` {function} - Function to trigger a refetch of the data.
 * - `headers` {any | null} - The headers returned by the server, or `null` if not available.
 */
const useQuery = <T,>(
  url: string,
  options: AxiosRequestConfig = config,
  enabled: boolean = true,
) => {
  const [isLoading, setLoading] = useState(enabled);
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [headers, setHeaders] = useState<Partial<AxiosResponseHeaders> | null>(
    null,
  );
  const abortControllerRef = useRef<AbortController>(new AbortController());

  const fetchData = useCallback(async () => {
    setData(null);
    setError(null);
    setLoading(true);
    try {
      const res = await API.get(url, {
        ...options,
        signal: abortControllerRef.current?.signal,
      });
      if (res?.data) setData(res.data);

      if (res?.headers) setHeaders(res?.headers);

      if (res?.data?.length === 0) setError('No data available');

      setLoading(false);
    } catch (err: any) {
      if (err?.name !== 'CanceledError') {
        const genericMsg = 'Something went wrong, please try again.';
        err?.response?.status === 500
          ? setError(genericMsg)
          : setError(err?.message || genericMsg);
        setLoading(false);
      }
    }
  }, [options, url]);

  useEffect(() => {
    if (!enabled) return;

    fetchData();

    const controller = abortControllerRef.current;
    return () => {
      controller?.abort();
      abortControllerRef.current = new AbortController();
    };
  }, [enabled, fetchData]);

  return {
    isLoading,
    data,
    error,
    setData,
    setError,
    setLoading,
    refetch: fetchData,
    headers,
  };
};

export default useQuery;
